import { AccountKindService } from '@frontendlib/common/services/account-kind.service';
import { claimDetailFooterSlot, provideDetailFooterSlot } from '@modules/common/components/detail-breadcrumbs/detail-footer-slot';
import { ItemChangesComponent } from '@modules/team-item-changes/item-changes.component';
import { ItemReviewComponent } from '@modules/common/components/item-review/item-review.component';
import {
  ITEM_REVIEW_INLINE_HOST,
  type ItemReviewTarget,
} from '@modules/common/components/item-review/item-review-dialog.service';
import { SpaceType } from '@sharedlib/common/types/SpaceType';
import { type StitchRequestConfig } from '@modules/common/services/base-stitch.service';
import { ChangeDetectorRef, input, viewChild } from '@angular/core';
import { ItemHandoverComponent } from '@modules/common/components/item-handover/item-handover.component';
import {
  ITEM_HANDOVER_INLINE_HOST,
  type ItemHandoverTarget,
} from '@modules/common/components/item-handover/item-handover-dialog.service';
import { UILocations } from '@modules/common/types/uiLocations';
import { itemDisplayIdentity } from '@modules/common/components/item-workspace/item-display-identity';
import { itemChangeRevision } from '@modules/common/components/item-workspace/item-change-revision';
import { ItemQuickCommentComponent } from '@modules/comments/components/item-quick-comment/item-quick-comment.component';
import { ItemWorkspaceChipComponent } from '@modules/common/components/item-workspace/item-workspace-chip.component';
import { ItemWorkspaceAccessComponent } from '@modules/common/components/item-workspace/item-workspace-access.component';
import { WorkspaceIdentityService } from '@modules/common/services/workspace-identity.service';
import { ItemWorkspaceFooterComponent } from '@modules/common/components/item-workspace/item-workspace-footer.component';
import { ContactVersionHistoryComponent } from '@modules/contacts/version-history/contact-version-history.component';
import { canEditDocumentsIn } from '@modules/files/version-history/file-edit-authority';
import { GroupSummariesCache } from '@modules/contacts/services/group-summaries.cache';
import { type StitchTypeToSummaryDtoType } from '@sharedlib/dtos/dto/StitchItemSummaryDtoRegistry';
import { APP_CRUMBS, DetailNavigation } from '@modules/common/components/detail-breadcrumbs/detail-navigation';
import { type DetailCrumb } from '@modules/common/components/detail-breadcrumbs/detail-breadcrumbs.component';
import { TitleByContactsFilterPipe } from '@modules/contacts/pipes/title-by-contacts-filter.pipe';
import { ContactsAppStateService } from '@modules/contacts/services/state.service';
import { itemWorkspace } from '@modules/common/components/item-workspace/item-workspace-scope';
import { ensureContactsTenantIdentity } from '@modules/contacts/services/contacts-tenant-identity';
import { IGNORE_SPACE_CONTEXT, ITEM_OWNER_ID, OWNING_SPACE_ID } from '@modules/common/interceptors/spaces.interceptor';
import { toSignal } from '@angular/core/rxjs-interop';
import { ContactsTenantScopeService } from '@modules/contacts/services/contacts-tenant-scope.service';
import { contactsOwnerContext } from '@modules/contacts/services/contacts-owner-context';
import {
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  type Observable,
  of,
  skip,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { Component, signal, computed, inject, Injector, Input, type Signal, type SimpleChanges } from '@angular/core';
import { FormArray } from '@angular/forms';
import { FullFormBaseComponent } from '@modules/common/components/full-form-base/full-form-base.component';
import { Tab } from '@modules/common/types/tab';
import { isNil } from '@modules/common/utils/base';
import { ContactsService } from '@modules/contacts/services/contacts.service';
import { GroupsService } from '@modules/contacts/services/groups.service';
import { Contact, type ContactFormGroupConfig } from '@modules/contacts/types/contact';
import { ContactEmail, ContactInfo, ContactPhone } from '@modules/contacts/types/contact-info';
import { Group } from '@modules/contacts/types/group';
import { DragDataTypes } from '@modules/drag-n-drop/types/drag-data';
import type { AutocompleteFactory } from '@frontendlib/common/types/autocomplete-factory';
import { contactInfoTypes } from '@modules/form-controls/types/contactInfoTypes';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { CONTACT_WEBSITE_MAX_LENGTH } from '@sharedlib/dtos/dto/contacts-app/contact.dto';

@Component({
  selector: 'app-contact-form',
  templateUrl: './contact-form.component.html',
  styleUrls: ['./contact-form.component.less'],
  standalone: false,
  providers: [
    {
      provide: ITEM_REVIEW_INLINE_HOST,
      useFactory: () => {
        const form = inject(ContactFormComponent);
        return { open: (target: ItemReviewTarget): boolean => form.openInlineReview(target) };
      },
    },
    WorkspaceIdentityService,
    // The Hub's lists hand their pager to the breadcrumbs row (Johanna, 2026-10-21: one row, not two).
    provideDetailFooterSlot(),
    {
      provide: ITEM_HANDOVER_INLINE_HOST,
      useFactory: () => {
        const form = inject(ContactFormComponent);
        return { open: (target: ItemHandoverTarget): boolean => form.openInlineAssignment(target) };
      },
    },
  ],
})
export class ContactFormComponent extends FullFormBaseComponent<StitchType.CONTACT, Contact, ContactFormGroupConfig> {
  protected readonly UILocations = UILocations;
  public readonly insideModal = input(false);
  /** New Contact in its create modal: no avatar tile, and the modals' title row close to the tabs (Johanna, 2026-10-21). */
  protected creatingInModal(): boolean {
    return this.insideModal() && !this.contact?.id;
  }
  public readonly reviewApi = inject(ContactsService).reviewApi;
  private readonly reviewControl = viewChild(ItemReviewComponent);
  protected readonly reviewContentVersion = itemChangeRevision(this.itemUpdated.pipe(map(() => undefined)));
  public openInlineReview(target: ItemReviewTarget): boolean {
    const control = this.reviewControl();
    if (
      target.stitchType !== StitchType.CONTACT ||
      !this.insideModal() ||
      !control ||
      target.itemId !== this.handoverItemId ||
      target.tenantId !== this.handoverTenantId
    )
      return false;
    control.openRequest();
    return true;
  }

  public readonly handoverApi = inject(ContactsService).handoverApi;
  protected readonly StitchType = StitchType;
  private readonly handoverControl = viewChild(ItemHandoverComponent);
  /** A hand-over is a team feature: a personal login gets none of its controls, whichever space is open. */
  private readonly accountKind = inject(AccountKindService);
  protected get handoverTenantId(): string | undefined {
    return this.accountKind.isTeam() && itemWorkspace(this.workspaceIdentity(), this.contact) === 'team'
      ? this.contact.accountId
      : undefined;
  }
  protected get handoverItemId(): string | undefined {
    return this.contact.id;
  }
  protected get handoverCanAssign(): boolean {
    return canEditDocumentsIn(this.permissionsService, this.handoverTenantId);
  }
  public openInlineAssignment(target: ItemHandoverTarget): boolean {
    const control = this.handoverControl();
    if (
      !this.handoverCanAssign ||
      !this.insideModal() ||
      !control ||
      target.itemId !== this.handoverItemId ||
      target.tenantId !== this.handoverTenantId
    )
      return false;
    control.openAssign();
    return true;
  }
  protected readonly workspaceRevision = itemChangeRevision(this.stitchItemChanged);

  protected readonly workspaceComponents = {
    'app-item-changes': ItemChangesComponent,
    'app-item-workspace-chip': ItemWorkspaceChipComponent,
    'app-item-workspace-access': ItemWorkspaceAccessComponent,
    'app-item-quick-comment': ItemQuickCommentComponent,
    'app-item-workspace-footer': ItemWorkspaceFooterComponent,
    'app-contact-version-history': ContactVersionHistoryComponent,
  };
  private readonly restoreChangeDetector = inject(ChangeDetectorRef);

  protected readonly sharingTenantId = signal<string | null>(null);
  private readonly contactsTenantScope = inject(ContactsTenantScopeService, { optional: true });

  private readonly itemIdentityService = inject(WorkspaceIdentityService, { optional: true });
  protected readonly workspaceIdentity = toSignal(
    this.stitchItemChanged.pipe(
      skip(1),
      take(1),
      switchMap(() =>
        this.spaceContextStore.type() === SpaceType.SHARED_SPACE
          ? (this.itemIdentityService?.current().pipe(take(1)) ?? of(undefined))
          : of(undefined),
      ),
      tap((identity) => {
        if (identity && !this.contact?.id) {
          this.sharingTenantId.set(
            this.contact?.accountId ?? this.contactsTenantScope?.effectiveTenantId() ?? identity.memberTenantId,
          );
        }
      }),
      catchError(() => of(undefined)),
    ),
    { initialValue: undefined },
  );
  protected readonly displayWorkspaceIdentity = itemDisplayIdentity(
    this.workspaceIdentity,
    this.itemIdentityService,
    this.stitchItemChanged,
    () => this.stitchItem,
    () => this.dp,
  );

  /**
   * The team that owns the saved contact, read from the contact's own tenant (#2387), in every host. The side panel,
   * the modal and the browser popup stand in a space store of their own, which can sit on no team or on another one,
   * so Activity cannot trust that store; one rule serves them all instead of a flag per host.
   */
  private readonly ownerTeamIdentity = toSignal(
    this.stitchItemChanged.pipe(
      map(() => {
        const saved = this.stitchItem;
        return saved?.id ? saved.accountId : undefined;
      }),
      distinctUntilChanged(),
      switchMap((owner) =>
        owner && this.itemIdentityService
          ? this.itemIdentityService.forItemOwner(owner).pipe(
              take(1),
              catchError(() => of(undefined)),
              startWith(undefined),
            )
          : of(undefined),
      ),
    ),
    { initialValue: undefined },
  );

  public changesKey = 'contact';
  tabs: Tab[] = [
    { title: 'Contact', value: 'contact' },
    { title: 'Hub', value: 'hub' },
    { title: 'Attachments', value: 'attachments' },
    { title: 'Stitch', value: 'stitch' },
    { title: 'Comments', value: 'comments' },
  ];
  protected override attachmentsMenuStateVariant = 'contacts';
  public readonly contactInfoTypes = contactInfoTypes;
  /** Matches the `Validators.maxLength` on `Contact.formGroupConfigFromSummary`'s `website` control. */
  protected readonly websiteValidationMessages = {
    maxlength: `Website must be ${CONTACT_WEBSITE_MAX_LENGTH} characters or fewer`,
  } as const;
  public suggestions: AutocompleteFactory<Group>;
  private createAudienceItem: Contact | undefined;
  private awaitingCreateAudience = false;
  private readonly cancelPendingCreate = new Subject<void>();
  private readonly settledTenant = toSignal(
    this.contactsTenantScope?.selectedTeamId?.() ?? of(this.contactsTenantScope?.tenantId?.()),
    { initialValue: undefined },
  );

  // Private
  protected dragDataType = DragDataTypes.contact;

  @Input() contact = new Contact();

  private readonly titleByContactsFilter = new TitleByContactsFilterPipe(inject(Injector));
  /** stitch-app-shell breadcrumbs, as in every app. */
  protected readonly navigation = new DetailNavigation(ContactsAppStateService, {
    appLabel: APP_CRUMBS.contacts,
    viewTitle: (folder) => this.titleByContactsFilter.transform(folder, null, true),
  });
  private readonly footerSlot = claimDetailFooterSlot(this.navigation.enabled);

  /** [workspace ›] Contacts app › the list's view › this contact. */
  protected breadcrumbs(): DetailCrumb[] {
    this.workspaceRevision();
    return this.navigation.crumbs({
      workspace: itemWorkspace(this.displayWorkspaceIdentity(), this.stitchItem),
      container: this.headerGroup()?.title,
      fallbackView: 'Contacts',
      itemTitle: this.contact.title || 'New Contact',
      itemId: this.contact?.id,
    });
  }

  /**
   * The group this contact sits in, named by the header's container badge. The summaries cache comes with a space
   * context; a host without one (a bare form) names no group.
   */
  protected readonly headerGroup: Signal<StitchTypeToSummaryDtoType<StitchType.GROUP> | undefined> =
    inject(GroupSummariesCache, { optional: true })?.getForItem(
      computed(() => {
        this.workspaceRevision();
        const contact = this.stitchItem;
        return contact?.groupId ? { id: contact.groupId, accountId: contact.accountId } : undefined;
      }),
    ) ?? signal(undefined);

  constructor(contactsService: ContactsService, groupsService: GroupsService, injector: Injector) {
    super(injector, contactsService);
    this.sharingTenantId.set(this.contactsTenantScope?.effectiveTenantId() ?? null);
    ensureContactsTenantIdentity(this.contactsTenantScope, injector, this.destroyRef);

    this.suggestions = (...args) => {
      const context = this.contact?.accountId
        ? contactsOwnerContext([this.contact])
        : (this.requestConfig().requestContext ??
          this.requestContext ??
          contactsOwnerContext(undefined, this.settledTenant()));
      return context ? groupsService.getAutocompleteSuggestions({}, () => context)(...args) : of([]);
    };
  }

  public handleAddItem(type: 'emails' | 'phones', index?: number): void {
    const control = this.form.controls[type];
    const newItem = ContactInfo.createItem(type);

    control.insert((!isNil(index) ? index : control.length) + 1, newItem);
  }

  public override ngOnChanges(changes: SimpleChanges): void {
    if (changes.contact && this.createAudienceItem && this.stitchItem !== this.createAudienceItem) {
      if (this.awaitingCreateAudience) {
        this.cancelPendingCreate.next();
        this.saveInProgress = false;
      }
      // A retained create audience belongs to that draft, never the next input in this reused host.
      this.requestContext = this.stitchItem?.accountId
        ? contactsOwnerContext([this.stitchItem])
        : changes.requestContext
          ? this.requestContext
          : undefined;
      this.createAudienceItem = undefined;
    }
    if (changes.contact && !this.stitchItem?.id) {
      const identity = this.workspaceIdentity();
      this.sharingTenantId.set(
        this.stitchItem?.accountId ?? this.contactsTenantScope?.effectiveTenantId() ?? identity?.memberTenantId ?? null,
      );
    }
    super.ngOnChanges(changes);
    if (changes.tab && this.tab === 'comments') this.openItemComments();
    if (changes.tab && this.tab === 'access' && this.insideTeam) this.openWorkspaceAccess();
    if (changes.tab && this.tab === 'versions') this.openVersionHistory();
  }

  public override handleCancel(): void {
    if (this.awaitingCreateAudience) {
      this.cancelPendingCreate.next();
      this.saveInProgress = false;
    }
    if (this.pristine) {
      return;
    }

    super.handleCancel();
    this.restoreEmails();
    this.restorePhones();
  }

  get stitchItem(): Contact {
    return this.contact;
  }

  protected get draftOwnerLocked(): boolean {
    const owner = this.stitchItem?.accountId;
    const identity = this.workspaceIdentity();
    return !!owner && owner !== identity?.spaceTenantId && owner !== identity?.memberTenantId;
  }

  protected setSharingTenantId(tenantId: string | null): void {
    const identity = this.workspaceIdentity();
    if (this.stitchItem.id || this.saveInProgress || this.draftOwnerLocked || this.form.controls.uploads.value.length)
      return;
    if (!identity || (tenantId !== identity.spaceTenantId && tenantId !== identity.memberTenantId)) return;
    if (tenantId === this.sharingTenantId()) return;
    this.sharingTenantId.set(tenantId);
    this.stitchItem.accountId = tenantId;
    this.stitchItem.groupId = null;
    this.form.controls.accountId.setValue(tenantId, { emitEvent: false });
    this.form.controls.groupId.setValue(null);
    this.requestContext = contactsOwnerContext(undefined, tenantId);
    this.originalFormValue = { ...this.originalFormValue, accountId: tenantId, groupId: null };
  }

  protected override tenantOwnerId(): string | undefined {
    if (!this.stitchItem?.id && this.workspaceIdentity() && !this.draftOwnerLocked) {
      return this.sharingTenantId() ?? this.stitchItem?.accountId;
    }
    return this.stitchItem?.accountId;
  }

  protected override requestConfig(): StitchRequestConfig {
    if (this.stitchItem?.accountId) return { tenantItems: [this.stitchItem] };
    const tenantId = this.workspaceIdentity() ? this.sharingTenantId() : null;
    return tenantId ? { requestContext: contactsOwnerContext(undefined, tenantId) } : super.requestConfig();
  }

  protected shouldRefreshList(prev, current) {
    return Contact.shouldRefreshList(prev, current);
  }

  /** A shell draft has no owner until Contacts identity answers; keep that audience for retries. */
  protected override performCreate(stitchItem: Contact): Observable<Contact> {
    this.createAudienceItem = this.stitchItem;
    const suppliedContext = this.requestConfig().requestContext;
    const suppliedOwner = suppliedContext?.get(ITEM_OWNER_ID) ?? suppliedContext?.get(OWNING_SPACE_ID);
    const carriedContext = this.stitchItem.accountId
      ? contactsOwnerContext([this.stitchItem])
      : suppliedOwner
        ? contactsOwnerContext(undefined, suppliedOwner)
        : suppliedContext?.get(IGNORE_SPACE_CONTEXT)
          ? suppliedContext
          : undefined;
    this.awaitingCreateAudience = true;
    const audience = carriedContext
      ? of(carriedContext)
      : (this.contactsTenantScope?.selectedTeamId() ?? of(null)).pipe(
          map((owner) => contactsOwnerContext(undefined, owner)),
        );
    return audience.pipe(
      filter((context) => context !== undefined),
      take(1),
      takeUntil(this.cancelPendingCreate),
      takeUntil(this.alive),
      tap((context) => {
        this.awaitingCreateAudience = false;
        this.requestContext = context;
        const owner = context.get(ITEM_OWNER_ID);
        if (owner) {
          this.stitchItem.accountId = owner;
          stitchItem.accountId = owner;
          this.form.controls.accountId.setValue(owner, { emitEvent: false });
        }
      }),
      switchMap(() => super.performCreate(stitchItem)),
      finalize(() => {
        this.awaitingCreateAudience = false;
      }),
    );
  }

  protected fromFormGroup(): Contact {
    return Contact.fromFormGroup(this.form);
  }

  protected override asFormGroup() {
    return this.contact.asFormGroup({ maxAttachmentSize: this.maxUploadSize() });
  }

  private restoreEmails(): void {
    const emailsArray = this.form.get('emails') as FormArray;
    if (!emailsArray) return;

    emailsArray.clear();

    if (this.stitchItem?.emails?.length) {
      this.stitchItem.emails.forEach((email: ContactEmail) => emailsArray.push(email.asFormGroup()));
    } else {
      emailsArray.push(new ContactEmail().asFormGroup());
    }
  }

  private restorePhones(): void {
    const phonesArray = this.form.get('phones') as FormArray;
    if (!phonesArray) return;

    phonesArray.clear();

    if (this.stitchItem?.phones?.length) {
      this.stitchItem.phones.forEach((phone: ContactPhone) => phonesArray.push(phone.asFormGroup()));
    } else {
      phonesArray.push(new ContactPhone().asFormGroup());
    }
  }
  protected get insideTeam(): boolean {
    return !!itemWorkspace(this.displayWorkspaceIdentity(), this.contact);
  }

  protected get workspaceFooterInputs(): Record<string, unknown> {
    return {
      identity: this.displayWorkspaceIdentity(),
      revision: this.workspaceRevision(),
      item: this.contact,
      noun: 'Contacts',
      collection: undefined,
      archived: this.form?.controls.archived.value ?? false,
      deleted: this.form?.controls.deleted.value ?? false,
    };
  }
  protected readonly openWorkspaceAccess = (): void => {
    this.selectedTab = 'access';
  };

  protected get workspaceAccessInputs(): Record<string, unknown> {
    return { identity: this.displayWorkspaceIdentity(), revision: this.workspaceRevision(), item: this.contact };
  }

  protected get workspaceChipInputs(): Record<string, unknown> {
    return { ...this.workspaceAccessInputs, openAccess: this.openWorkspaceAccess, compact: this.contracted };
  }

  protected get itemChangesInputs(): Record<string, unknown> | undefined {
    const item = this.stitchItem;
    if (!item?.id || !item.accountId || !this.ownedByATeam(item)) return undefined;
    return { path: '/api/contacts/contacts', itemId: item.id, tenantId: item.accountId };
  }

  /** A team's own contact: the space this form stands in, or the team the contact itself belongs to (panel, modal, popup). */
  private ownedByATeam(item: Contact): boolean {
    return (
      (this.spaceContextStore.type() === SpaceType.SHARED_SPACE &&
        item.accountId === this.spaceContextStore.contextId()) ||
      itemWorkspace(this.ownerTeamIdentity(), item) === 'team'
    );
  }

  protected get workspaceTabs(): Tab[] {
    const tabs = this.tabsWithCounts().flatMap((tab) =>
      tab.value === 'comments' && this.itemChangesInputs ? [tab, { title: 'Activity', value: 'activity' }] : [tab],
    );
    const versioned = this.historyAvailable() ? [...tabs, { title: 'Version history', value: 'versions' }] : tabs;
    return this.insideTeam && this.selectedTab === 'access'
      ? [...versioned, { title: 'Access', value: 'access' }]
      : versioned;
  }

  /** A team's own saved contact inside that team (#1951, TW-COLLAB-11), as Activity is offered. */
  protected historyAvailable(): boolean {
    return !!this.itemChangesInputs;
  }

  protected get versionHistoryInputs(): Record<string, unknown> {
    return {
      contact: this.contact,
      // The role's Edit Documents rule in the contact's own workspace (#2069's rule; the file history reads it the same way).
      canEdit: canEditDocumentsIn(this.permissionsService, this.contact.accountId),
      held: this.saveInProgress || !this.pristine,
      restored: this.applyRestoredContact,
    };
  }

  protected readonly openVersionHistory = (): void => {
    if (this.historyAvailable()) this.selectedTab = 'versions';
  };

  /** A restore answer is the contact's current details; the form shows them at once. */
  private readonly applyRestoredContact = (answer: Partial<Contact>): void => {
    const fresh = new Contact(answer);
    const contact = this.contact;
    if (fresh.id !== contact.id || fresh.accountId !== contact.accountId) return;
    const pristine = this.pristine;
    Object.assign(contact, fresh);
    if (pristine) this.stitchItemChanged.next();
    this.itemUpdated.emit(contact);
    this.restoreChangeDetector.markForCheck();
  };
  protected readonly openItemComments = (): void => {
    this.selectedTab = 'comments';
  };

  protected get quickCommentInputs(): Record<string, unknown> {
    return {
      identity: this.displayWorkspaceIdentity(),
      revision: this.workspaceRevision(),
      item: this.contact,
      noun: 'contact',
      selectedTab: this.selectedTab,
      openComments: this.openItemComments,
    };
  }
}
